module.exports={render:function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _vm._m(0)
},staticRenderFns: [function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _c('div', {
    staticClass: "chapter"
  }, [_c('div', {
    staticClass: "inner_page"
  }, [_c('section', {
    attrs: {
      "epub:type": "chapter",
      "title": "1. The Rise of the Singapore Naval Base"
    }
  }, [_c('p', {
    staticClass: "chtit",
    attrs: {
      "id": "ch-7"
    }
  }, [_c('span', {
    staticClass: "right_1",
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_9",
      "title": "9"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/9780253044174_Contents#ch7"
    }
  }, [_c('b', [_vm._v("1 | The Rise of the Singapore Naval Base")])])])]), _vm._v(" "), _c('p', {
    staticClass: "noindent"
  }, [_vm._v("Singapore, the Cape of Good Hope, Alexandria, Gibraltar, and Dover, in the opinion of Lord Fisher (Admiral John Fisher, First Sea Lord, 1904–10), constituted the five “keys” of the world that gave the United Kingdom command over the Indian Ocean and the Mediterranean. Control over the Cape of Good Hope, Aden, and Singapore made the Indian Ocean almost a “British Lake.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-1",
      "id": "fn1-1"
    }
  }, [_vm._v("1")])]), _vm._v(" This chapter portrays the gradual development of the Singapore Naval Base and the shifting threat to it during the interwar period. The failure of the British high command to come up with a coherent defensive strategy is highlighted. This chapter is divided into two parts. The first part deals with the evolution of defense policy regarding Singapore during the 1920s and the mid-1930s. The second half charts the complications that resulted in the defense of Singapore Naval Base, due to an increase in the range of aircraft and the rise of the Japanese threat in the Far East from the late 1930s until the beginning of the Pacific War.")]), _vm._v(" "), _c('p', {
    staticClass: "h2"
  }, [_c('b', [_vm._v("Evolution of Singapore’s Defense in the Interwar Era")])]), _vm._v(" "), _c('p', {
    staticClass: "noindent"
  }, [_vm._v("Singapore was acquired from the Sultan of Johore in 1819 by Thomas Stamford Raffles on behalf of the East India Company and had been transformed by the end of the First World War from an uninhabited island to one of the greatest transit ports in the world."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-2",
      "id": "fn1-2"
    }
  }, [_vm._v("2")])]), _vm._v(" For the first 140 years of British occupation, Singapore was a drain on the East India Company’s budget. In 1818, Raffles was appointed as the governor of Bencoolen (in Indonesia) on the southwest coast of Sumatra. The British plan at that time was to expand the China trade. The route from British India to China had to pass through Southeast Asia. Since the late seventeenth century, Britain’s commercial rival Holland had dominated Southeast Asia’s sea lanes. Holland had succeeded in closing all the British trading posts in this region except Bencoolen. Raffles thought that the British could attract Malay trade to Singapore. Singapore (traditional name Singapura) was in fact an old Malay trading port that had declined, and Raffles wanted to rejuvenate it. The British merchant community in India also enthusiastically supported the acquisition of Singapore. Initially, Raffles was afraid of a Dutch attack against Singapore. In 1824, in accordance with the Treaty of London, the British agreed to evacuate Bencoolen, and in return the Dutch accepted British primacy in the Malayan Peninsula and in Singapore. After that, no European power posed any threat to Singapore, at least until the end of the First World War."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-3",
      "id": "fn1-3"
    }
  }, [_vm._v("3")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_c('span', {
    staticClass: "right_1",
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_10",
      "title": "10"
    }
  }, [_vm._v("Meanwhile, British power in the Malayan Peninsula was also increasing. British acquisitions in Malaya could be divided into three categories. First, there was the colony of the Straits Settlements of Penang with Province Wellesley, Malacca, and Singapore. This was a British territory governed as a Crown colony by a governor, acting through a legislative council and an executive council. The second group consisted of the Federated Malay States of Perak, Selangor, Negri Sembilan, and Pahang, a British-protected and British-administered Malay territory. The governor of the Straits Settlements also acted as the high commissioner of the Malay States. In the Federated Malay States, the British exercised direct administration and complete control except in matters of Malay customs and Islamic religion. The third category comprised the five Unfederated Malay States of Johore, Kedah, Perlis, Kelantan, and Trengganu. These states were governed by their own sultans, who were assisted by British advisors. The four northern states of Perlis, Kedah, Kelantan, and Trengganu were under Siamese suzerainty until 1909, when an agreement was made between the British and Siamese governments, as a result of which all these four polities came under the protection of the British Crown. After 1914, Johore concluded a similar treaty with the British. Among these five Unfederated Malay States, Johore enjoyed the greatest autonomy. All these states were incorporated within the British Empire through separate treaties. In these treaties, Britain accepted the obligation to defend these states against external aggression. Overall, regarding civil affairs the Malay states were under the high commissioner, who reported to the Colonial Office and in matters of defense came under the British GOC in Malaya, who reported to the War Office in London."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-4",
      "id": "fn1-4"
    }
  }, [_vm._v("4")])])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("In August 1919, Admiral of the Fleet Lord John Jellicoe, who had commanded the British Grand Fleet between 1914 and 1916 and was then First Sea Lord, considered Japan to be the “Germany of the Far East.” Japan’s commercial penetration into India was disadvantageous to Britain. He predicted that Japanese and British interests were bound to clash."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-5",
      "id": "fn1-5"
    }
  }, [_vm._v("5")])]), _vm._v(" In 1919, he toured the Pacific and recommended the construction of a naval base at Singapore."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-6",
      "id": "fn1-6"
    }
  }, [_vm._v("6")])]), _vm._v(" The RN put forward the War Memorandum (Eastern), which was a plan for a possible Anglo-Japanese War in 1920. The aim was to redeploy the RN from Europe to Asia in order to obtain a decisive victory over the Imperial Japanese Navy (IJN). This plan in turn provided a rationale for making Singapore a naval base to support the fleet operation. Further, this plan also required the use of Hong Kong as an advance base for blockading Japan. However, Churchill at that time questioned the wisdom of this plan. He argued that a long and costly war against Japan was hardly viable or worthwhile. In December 1919, Britain maintained one aircraft carrier, one destroyer, one sub tender, one minesweeper, one submarine, one heavy cruiser, and three light cruisers at the China Station."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-7",
      "id": "fn1-7"
    }
  }, [_vm._v("7")])]), _vm._v(" Historian Christopher M. Bell asserts that until the 1920s and the early 1930s, the Singapore Naval Base was intended to provide the docking and repair facilities for a British fleet operating in the Far Eastern waters, but it was not expected to serve as the principal base from which to launch operations against Japan. This role was ideally to have been fulfilled by Hong Kong. At that time, defense of Hong Kong was considered difficult but not impossible."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-8",
      "id": "fn1-8"
    }
  }, [_vm._v("8")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_c('span', {
    staticClass: "right_1",
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_11",
      "title": "11"
    }
  }, [_vm._v("Actually, as early as 1919, the British cabinet’s Standing Defence Sub-Committee had proposed abandoning Hong Kong as a main fleet base and concentrating on Singapore."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-9",
      "id": "fn1-9"
    }
  }, [_vm._v("9")])]), _vm._v(" Hong Kong in the 1920s was the greatest imperial port outside the British Isles. It was the “door to the Far Eastern markets.” However, Hong Kong was sidelined for several reasons. Britain could not afford to maintain a garrison of forty thousand troops, considered necessary to secure this island against a Japanese invasion. Also, Hong Kong is 1,600 miles from Japan, while Singapore is 2,900 miles from Japan. Though the Japanese could easily mount an invasion of Hong Kong from the Chinese mainland, this was not possible in the case of Singapore. Singapore was chosen because it was considered the gateway to the Pacific, on the shortest possible route from the Indian Ocean to the Pacific Ocean, and an ideal base for a fleet seeking to guard against probable Japanese attack on the trade of the Indian Ocean, particularly the trade route from India to Australia, and to hold open the line of advance toward Japan through the Malay Barrier."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-10",
      "id": "fn1-10"
    }
  }, [_vm._v("10")])])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Japan was Britain’s chief commercial rival in the Far East, and during the First World War, it pushed its trade forward at Britain’s expense."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-11",
      "id": "fn1-11"
    }
  }, [_vm._v("11")])]), _vm._v(" As early as December 1920, the Admiralty pointed out that an aggressive move by Japan in the Far East that coincided with a threat to British security in Europe posed an insoluble problem."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-12",
      "id": "fn1-12"
    }
  }, [_vm._v("12")])]), _vm._v(" The Anglo-Japanese Treaty of 1902 lapsed in 1921."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-13",
      "id": "fn1-13"
    }
  }, [_vm._v("13")])]), _vm._v(" The Anglo-Japanese alliance was based on the fact that Czarist Russia was the principal enemy of Britain in the Far East. In accordance with this alliance, if Russia moved against British India from Central Asia, Japan would aid Britain. Also, if Russia moved against Japan in Manchuria, then Britain would aid Tokyo. Japan defeated Russia during the 1904–5 Russo-Japanese War, and the reduction of Russian strength enabled Britain to shift some naval assets in 1911 from the China Squadron to the North Sea in order to meet the rising challenge of the German High Sea Fleet. However, after the end of the First World War, the decline of Russian strength in the Far East and the rise of Japan as the third naval power in the world changed the strategic calculus for Britain. Further, Britain was unwilling to continue its alliance with Japan because the United States considered Tokyo a threat and a competitor."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-14",
      "id": "fn1-14"
    }
  }, [_vm._v("14")])]), _vm._v(" On 2 May 1921, the Standing Defence Sub-Committee of the cabinet, chaired by Arthur James Balfour (prime minister of Britain from 1902 to 1905 and foreign secretary from 1916 to 1919), considered the Admiralty and War Office papers of October 1919 and March 1920. He concluded that Hong Kong was practically indefensible and agreed to develop Singapore as Britain’s principal Far Eastern naval base in view of its strategic importance and lesser vulnerability. Balfour suggested that the Overseas Defence Committee discuss the argument not only against Hong Kong but also against Sydney as the probable Eastern British naval base. Sydney did not satisfy the offensive criterion due to distance, as a fleet based there could not operate in Japanese waters, nor could it prevent the intrusion of Japanese naval units in the Indian Ocean through the Malay Barrier and the consequent severing of its own line of communication (LoC) through Singapore, Colombo to Aden."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-15",
      "id": "fn1-15"
    }
  }, [_vm._v("15")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("On 16 June 1921, the British government decided to construct a naval base at Seletar on the northern shore of Singapore Island to protect the British Far "), _c('span', {
    staticClass: "right_1",
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_12",
      "title": "12"
    }
  }, [_vm._v("Eastern possessions against a rising Japan. The naval base was to be located on the estuary of Sembawang River. Singapore Naval Base was to be used as a fueling and repair base for the main British fleet in case of war with Japan. It was approached by the eastern arm of the Johore Straits and was within the hand-held arms (rifles and machine guns) range of the mainland of the Malayan peninsula."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-16",
      "id": "fn1-16"
    }
  }, [_vm._v("16")])])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Singapore had no significant agricultural production. Food supplies were imported from the Malayan hinterland and Thailand."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-17",
      "id": "fn1-17"
    }
  }, [_vm._v("17")])]), _vm._v(" Malaya was vital not only for providing protection to the British naval base in Singapore but also because it produced 40 percent of the rubber and over half of the world’s tin."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-18",
      "id": "fn1-18"
    }
  }, [_vm._v("18")])]), _vm._v(" Britain obtained 70 percent of its rubber and 57 percent of its tin from Malaya."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-19",
      "id": "fn1-19"
    }
  }, [_vm._v("19")])]), _vm._v(" However, at that time, the range of land-based aircraft was limited. For attacking Singapore, the Japanese had to have airfields near Mersing on the east coast of Malaya. In the 1920s, of course, Japanese forces were not occupying either south China or French Indochina. The British therefore concluded that the RN’s control of the sea approaches to Singapore would be sufficient to protect the naval base there. If a threat by sea arose, major naval reinforcements could reach Singapore from Britain within six weeks. The British thus concluded that, in the absence of any major threat by land, only a small garrison of ground forces would be necessary at Singapore."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-20",
      "id": "fn1-20"
    }
  }, [_vm._v("20")])]), _vm._v(" In 1921, the British strategic managers believed that Singapore might be threatened by a squadron of armored cruisers and a raiding force of about two thousand men. In 1922, the Admiralty assumed that the Japanese could send six battleships against Singapore. The Washington Conference (November 1921) agreed to limit naval armaments on a 5:5:3 basis for the United States, Great Britain, and Japan. The Washington Treaty was signed by Britain, France, Italy, Japan, and the United States in 1922. The signatories agreed to maintain the status quo in Asia and to limit warship construction. In the Washington Naval Treaty of 1922, Britain formally surrendered its long tradition of maritime supremacy."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-21",
      "id": "fn1-21"
    }
  }, [_vm._v("21")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("The Admiralty’s 1924 Eastern War Memorandum prescribed that if Singapore was lost, the fleet would be immobilized for want of fuel and would be incapable of relieving the pressure on Hong Kong in time to save it from falling into the hands of the Japanese. But as long as Singapore was held, the British could maintain a presence in the region even if Hong Kong was occupied. So the safety of Singapore became the keynote of British Far Eastern strategy."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-22",
      "id": "fn1-22"
    }
  }, [_vm._v("22")])]), _vm._v(" In line with this thinking, at the beginning of the Far Eastern War, British grand strategy gave more importance to Singapore as compared to Hong Kong. The Singapore Sub-Committee Report of 1925 stated that due to difficult terrain, the approach of a hostile force through the State of Johore would be attended with numerous difficulties, and the scenario would favor the defenders. The consensus was that the overland threat through the Malayan Peninsula to Singapore was not serious, and the main threat was perceived to come from the sea and landings on Singapore Island itself. The jungle north of Singapore Island was considered impassable for hostile infantry."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-23",
      "id": "fn1-23"
    }
  }, [_vm._v("23")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_c('span', {
    staticClass: "right_1",
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_13",
      "title": "13"
    }
  }, [_vm._v("In 1928, the Royal Air Force (RAF) came to Singapore,"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-24",
      "id": "fn1-24"
    }
  }, [_vm._v("24")])]), _vm._v(" but the limited range of military aircraft at that time still enabled the fleet to control the Sea Line of Communication (SLoC)."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-25",
      "id": "fn1-25"
    }
  }, [_vm._v("25")])]), _vm._v(" While the RN wanted to defend the naval base with fixed heavy batteries, the RAF wanted torpedo bombers escorted by fighters and reconnaissance aircraft for defense."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-26",
      "id": "fn1-26"
    }
  }, [_vm._v("26")])]), _vm._v(" In May 1932, the Sub-Committee of the Committee of Imperial Defence emphasized the importance of shore-based guns as a deterrent against a hostile naval threat and identified the RAF as the secondary weapon of defense for Singapore."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-27",
      "id": "fn1-27"
    }
  }, [_vm._v("27")])])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("By the 1930s, the increase in the range of land-based aircraft and the potential threat posed by Japanese carrier-based aircraft led to a reassessment of strategy."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-28",
      "id": "fn1-28"
    }
  }, [_vm._v("28")])]), _vm._v(" In April 1933, Japan withdrew from the League of Nations."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-29",
      "id": "fn1-29"
    }
  }, [_vm._v("29")])]), _vm._v(" Japan renounced the Washington naval limitations in 1934."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-30",
      "id": "fn1-30"
    }
  }, [_vm._v("30")])]), _vm._v(" At that time, the only RAF airfield was at Seletar in Singapore. Construction of two further airfields at Singapore Island was immediately started. It was decided to build aerodromes on the east coast with the objective of bombing hostile troopships in case they approached the east coast of Malaya to make landfall. Due to the limited range of aircraft and the difficulties of flying over the mountain range in central Malaya, especially during bad weather, the RAF insisted on building airports on the east rather than along the west coast of Malaya."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-31",
      "id": "fn1-31"
    }
  }, [_vm._v("31")])]), _vm._v(" In 1935, new airfields were planned at Kahang in Johore, Kuantan, and Kota Bahru/Bharu in Malaya to cover the approaches from the South China Sea."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-32",
      "id": "fn1-32"
    }
  }, [_vm._v("32")])]), _vm._v(" The construction of these aerodromes increased the responsibility of the army to protect them.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Britain’s breach with Italy posed a potential danger to British interests in the Mediterranean (the link between Europe and Britain’s Asian possessions) and the Middle East (Britain’s source of oil). This development further exacerbated the issue of defense of the Far East."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-33",
      "id": "fn1-33"
    }
  }, [_vm._v("33")])]), _vm._v(" Besides the worsening scenario in Europe, Japan became more aggressive in Asia in the 1930s. In the London Naval Conference of 1930, Britain and the United States had proposed a 5:5:3 ratio for cruisers of the United States, Britain, and Japan. However, Japan had demanded a 10:10:7 cruiser ratio. In the end, the Japanese had accepted the Western powers’ proposed ratio for building cruisers, but on 15 January 1936, Japan walked out of the London Naval Conference."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-34",
      "id": "fn1-34"
    }
  }, [_vm._v("34")])]), _vm._v(" The Washington Naval Treaty expired in 1936."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-35",
      "id": "fn1-35"
    }
  }, [_vm._v("35")])]), _vm._v(" Bell asserts that the rise of a single European challenger need not have prevented the transfer of a British fleet to the Far East. During the 1920s, Britain possessed a substantial margin of naval superiority over its rivals, and London might have maintained a large fleet in the Far East and still dominated European waters. This possibility was undermined by the rise of a triple threat from Germany, Italy, and Japan in the mid-1930s. In that period, the problem facing the British planners was not whether they could send a fleet to the Far East but whether they could dispatch enough ships for an offensive strategy or only those adequate for a defensive one. British naval strength was still sufficient to allow for a vigorous defensive stance in both regions simultaneously. As long as Britain had a reliable ally (France) in Europe, London was in a position to assume the offensive in one of these theaters."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-36",
      "id": "fn1-36"
    }
  }, [_vm._v("36")])]), _vm._v(" Until 1936, defenses were constructed to meet an attack on "), _c('span', {
    staticClass: "right_1",
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_14",
      "title": "14"
    }
  }, [_vm._v("Singapore from the sea. But from 1937 onward, Malaya Command believed more and more that a hostile attack would come from the north."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-37",
      "id": "fn1-37"
    }
  }, [_vm._v("37")])])])]), _vm._v(" "), _c('p', {
    staticClass: "h2"
  }, [_c('b', [_vm._v("Singapore and the Strategic Context before the Far Eastern War")])]), _vm._v(" "), _c('p', {
    staticClass: "noindent"
  }, [_vm._v("According to Lieutenant General Lewis Heath, commanding officer (CO) of the III Indian Corps during 1941–42, an enemy intent on capturing Singapore would not attack across the eastern shore of the island, due to the presence of the coastal guns, but would make a landfall on the eastern coast of Malaya. The army, for Heath, was supposed to play a minor role in the defense of Singapore. The RN was to play the premier role, but after 1934, the probability of a fleet being dispatched to the Far East started declining. Then, writes Heath, the RAF took over the duty of protecting Singapore. Arthur Ernest Percival disagrees and asserts that it was only in 1940 when it became evident that a fleet could not be sent to Singapore and the RAF took over the principal duty of protecting the naval base."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-38",
      "id": "fn1-38"
    }
  }, [_vm._v("38")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("In 1937, defense policy was based on the assumption that the British main fleet would arrive from northwestern Europe within 70 days, and the army’s job was to hold out on the island for that period."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-39",
      "id": "fn1-39"
    }
  }, [_vm._v("39")])]), _vm._v(" In 1921, London had estimated that the main fleet would arrive in Singapore in the case of war in the Far East within 42 days; in 1937, however, due to the deteriorating strategic situation as a result of the rise of the Italian-German threat in Europe, the period of relief had increased by another 28 days. In late 1937, Colonel Percival, then chief staff officer of Major General William Dobbie (GOC Malaya since 1935), in a report confirmed that north Malaya might become the battleground; the Japanese might capture the east-coast landing sites in Siam (Thailand) and Malaya to acquire the aerodromes and gain local air superiority."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-40",
      "id": "fn1-40"
    }
  }, [_vm._v("40")])]), _vm._v(" Percival noted that the territory of southern Siam might be used by the Japanese to invade Malaya with the ultimate aim of capturing Singapore. Japanese occupation of southern Siam would enable them to cut off Singapore’s food supply. Further, as Percival correctly noted, it would allow the Japanese to establish airfields from where Japanese aircraft could threaten British aerial superiority over Malaya. Therefore, Britain should be prepared to use diplomatic force. And if necessary, Britain should be ready to use naval and aerial force to prevent the Japanese from occupying southern Siam. Hence, to protect Singapore, Johore and northern Malaya needed to be defended. Percival continued that at the time the defense of northern Malaya had been entrusted to the Malay States Volunteer Force, assisted by certain units of the Straits Settlement and of the Unfederated Malay States, and the defense of Johore was in the hands of the Johore military and Johore Volunteer Engineers. Percival considered these forces to be weak. He wanted regular infantry assisted by Royal Engineers (RE) and Royal Artillery (RA) for the defense of Penang and southern Malaya. Percival for the time being was not underrating the Japanese military machine. He pointed out that the amphibious expeditions launched by the Japanese along the coast of China were worth examining. He lauded such Japanese expeditions as combined operations including air force, infantry, and "), _c('span', {
    staticClass: "right_1",
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_15",
      "title": "15"
    }
  }, [_vm._v("navy. In the near future, he feared, the Japanese might land infantry and tanks with special landing crafts along Changi and Singapore City. Percival was worried that the Japanese might conduct nocturnal landing operations. To counter such a threat, Percival demanded two infantry battalions for Singapore Island."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-41",
      "id": "fn1-41"
    }
  }, [_vm._v("41")])])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("On 14 February 1938, the naval base in the northeast corner of Singapore Island was opened."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-42",
      "id": "fn1-42"
    }
  }, [_vm._v("42")])]), _vm._v(" The dry dock was completed but construction work continued until the outbreak of the Far Eastern War in 1941."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-43",
      "id": "fn1-43"
    }
  }, [_vm._v("43")])]), _vm._v(" In May 1938, Dobbie also proposed that an attack from the north toward Singapore posed a great danger."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-44",
      "id": "fn1-44"
    }
  }, [_vm._v("44")])]), _vm._v(" Interestingly, Dobbie pointed out that the Japanese could land during the northeast monsoon, and the jungle of Malaya in most cases was not impassable. He was in favor of establishing a defensive line along the Johore River, Kota Tinggi, Kulai, and Pulai River."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-45",
      "id": "fn1-45"
    }
  }, [_vm._v("45")])]), _vm._v(" This defense line was considered to be adequately far away from Singapore to prevent the Japanese artillery from shelling the island. For protecting this line, he demanded fifteen tanks and sixteen (at that time he had four) armored cars."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-46",
      "id": "fn1-46"
    }
  }, [_vm._v("46")])]), _vm._v(" In August 1939, Dobbie was succeeded by Major General Lionel V. Bond."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-47",
      "id": "fn1-47"
    }
  }, [_vm._v("47")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Historian Malcolm H. Murfett rightly says that the bulk of the RN could not afford to sit more than eight thousand nautical miles away from its home bases for months at a time when there was a real threat to the security of the British Isles posed by Nazi Germany."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-48",
      "id": "fn1-48"
    }
  }, [_vm._v("48")])]), _vm._v(" After rejecting the cruiser limitations at the 1930 London Naval Conference, the Italian and German navies had expanded, and they in turn became a serious threat to the RN. By 1940, the Italian Navy had six battleships, seven heavy cruisers, twelve light cruisers, sixty-one fleet destroyers, and 105 submarines."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-49",
      "id": "fn1-49"
    }
  }, [_vm._v("49")])]), _vm._v(" In January 1938, the War Office noted the fact that the Japanese, being heavily engaged in China, must be taken into consideration while assessing the threat to Singapore. Further, the British main fleet would sail for Singapore regardless of the situation in the European waters."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-50",
      "id": "fn1-50"
    }
  }, [_vm._v("50")])]), _vm._v(" However, the strategic situation was changing quickly. In March 1939, Churchill, in a lengthy memorandum to British Prime Minister Neville Chamberlain, noted that the Mediterranean was more important than the Far East as far as deployment of the RN was concerned."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-51",
      "id": "fn1-51"
    }
  }, [_vm._v("51")])]), _vm._v(" Later, as prime minister, Churchill would hold on to this aspect of strategy. On 5 April 1939, Vice Admiral Andrew Cunningham, the deputy chief of Naval Staff of Britain reported that naval strength was at rock bottom, with only ten out of the fifteen capital ships being available for immediate operation."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-52",
      "id": "fn1-52"
    }
  }, [_vm._v("52")])]), _vm._v(" However, British naval strength, though declining in the Far East, was not negligible. In June 1939, the RN maintained one aircraft carrier, one sub tender, two destroyers, three heavy cruisers, one light cruiser, and four submarines in the China Station. In addition, there were thirteen long-range submarines either in Hong Kong or in Singapore, which could threaten Japanese shipping around the home islands."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-53",
      "id": "fn1-53"
    }
  }, [_vm._v("53")])]), _vm._v(" This naval detachment could not be totally brushed aside by the IJN. But with the beginning of the European War in September 1939, British naval strength in the Far East was considerably reduced. Further, in 1939, the American policy makers gave more attention to European affairs than to the Far East."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-54",
      "id": "fn1-54"
    }
  }, [_vm._v("54")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("In June 1939, there were forty British aircraft in Singapore, all of them obsolete."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-55",
      "id": "fn1-55"
    }
  }, [_vm._v("55")])]), _vm._v(" At that time, the Admiralty considered that at best only two British capital "), _c('span', {
    staticClass: "right_1",
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_16",
      "title": "16"
    }
  }, [_vm._v("ships could be sent to the Far East without weakening the defense of home waters and the Mediterranean."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-56",
      "id": "fn1-56"
    }
  }, [_vm._v("56")])]), _vm._v(" In December 1941, this strategy would be put into effect. In July 1939, it was postulated that the main fleet would arrive within 90 days after the outbreak of hostilities in the Far East, and three months later the period was extended to 180 days."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-57",
      "id": "fn1-57"
    }
  }, [_vm._v("57")])]), _vm._v(" In February 1942 the Japanese would arrive at Singapore in a campaign lasting 55 days.")])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("The Chiefs of Staff Committee (CoSC) did not follow any long-term coherent plan for strengthening the air defense of Singapore. Rather, their policy wavered with time. On 18 March 1940, the CoSC assessed that the chance of war breaking out with Japan had decreased. So the CoSC ordered that the two Blenheim medium bomber squadrons that had been transferred from India to Singapore should be shifted to Iraq or Egypt."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-58",
      "id": "fn1-58"
    }
  }, [_vm._v("58")])]), _vm._v(" In June, one of these squadrons was sent to Egypt and another to Aden."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-59",
      "id": "fn1-59"
    }
  }, [_vm._v("59")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("In April 1940, Major General Bond (GOC Malaya), unlike Dobbie, believed that the defense of the whole of Malaya was necessary in order to protect Singapore. For this task, he demanded between thirty-nine and forty-two infantry battalions. Bond noted that the Japanese could establish their bases in south Indochina. So northern Malaya needed to be defended against a substantial hostile force, which in turn would require at least forty battalions and two tank regiments. However, noted Bond, if the RAF could assure a vigorous offensive against the enemy force, then twenty-five battalions with three antitank (A/T) batteries would suffice. And he had only nine regular infantry battalions in his possession. He was against the opinion that the RAF should take over the primary responsibility for defense of Malaya as in Iraq. But the RAF, in Bond’s view, was made responsible for the destruction of the Japanese expedition before it landed. And if this task proved impossible for the RAF, then at least by repeated bombardment, the air force should ensure that no base could be maintained by the Japanese within striking distance of British aerodromes. Being a realist, Bond understood that he would not get much help from the RN as the main fleet might not be sent from Britain to Malaya-Singapore in time."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-60",
      "id": "fn1-60"
    }
  }, [_vm._v("60")])]), _vm._v(" For the first time, the army was coming in a big way for the defense of Singapore Island. But the relationship between Bond and Air Officer Commanding (AOC) Air Vice Marshal J. T. Babington was sour,"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-61",
      "id": "fn1-61"
    }
  }, [_vm._v("61")])]), _vm._v(" which in turn prevented intimate joint planning between the RAF and the army for the defense of Malaya-Singapore.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("On 10 May 1940, Churchill became the British prime minister, the same day that the Battle of France and the Low Countries began. The campaign started badly for the Allies. It seemed the Germans might also invade Britain after the collapse of France. The secretary of state for Dominion Affairs admitted that as things stood now, there seemed to be no prospect of sending a fleet to Singapore."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-62",
      "id": "fn1-62"
    }
  }, [_vm._v("62")])]), _vm._v(" On 25 June 1940, the CoSC, comprising Air Chief Marshal C. L. N. Newall, General (later Field Marshal) John Dill (chief of the Imperial General Staff/CIGS), and Admiral T. S. V. Phillips (vice chief of Naval Staff), noted that in those circumstances, a fleet could not be spared for the Far East. The only option was to strengthen the land and air defense in Malaya. In the worst possible scenario, the objective was to retain a foothold in that area. Supply of troops from "), _c('span', {
    staticClass: "right_1",
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_17",
      "title": "17"
    }
  }, [_vm._v("the United Kingdom would prejudice the defense of Britain, and troops could not be spared from the Middle East. The only possible sources of troops were India and Australia. India, it was estimated, could make one brigade available immediately and one division in September. These troops, who were originally earmarked for service in Iraq and Iran, were to be diverted to Malaya. Australia promised to send five thousand men in July, another thirty-four thousand in August, and two squadrons of aircraft. At this stage the CoSC emphasized the retention of the garrison at Hong Kong. The three members of the Chiefs of Staff ended their report by noting: “We recommend that authority be accorded now to the Governor of Malaya to institute the necessary measures to bring the Malayan defenses up to a state of readiness, which will enable them to be fully effective within five days of warning.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-63",
      "id": "fn1-63"
    }
  }, [_vm._v("63")])])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("On 27 July 1940, the CoSC warned that since Britain was heavily engaged in Europe and in the Middle East, all possible steps must be taken to avoid an open clash with Japan. The committee advised considerable concessions to Japan, such as the closure of the Burma Road, as part of the general settlement. And due to a shortage of resources, occupation of Indochina by Japan could not be regarded as a casus belli; war should be started only in case of a direct Japanese attack to any British possessions."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-64",
      "id": "fn1-64"
    }
  }, [_vm._v("64")])]), _vm._v(" The CoSC accepted that in the absence of a fleet, the defense of Malaya-Singapore had to depend primarily on air power. It was accepted that for the defense of the naval base, it was no longer sufficient merely to concentrate upon the defense of Singapore Island, but it was necessary to hold the whole of Malaya. The objective was to replace the 88 obsolete aircraft with 336 modern first-line aircraft by the end of 1941. This contingent did not include the aircraft necessary for the defense of Burma, and any augmentation of aircraft for Hong Kong was also turned down."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-65",
      "id": "fn1-65"
    }
  }, [_vm._v("65")])]), _vm._v(" Interestingly, the CoSC in their report neglected the crucial issue of where these new aircraft were to come from.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("As early as 1936, French attention was concentrated on Europe, and defense of the Far East was considered an unaffordable luxury."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-66",
      "id": "fn1-66"
    }
  }, [_vm._v("66")])]), _vm._v(" On 27 September 1940, Japan signed the Tripartite Pact with Germany and Italy."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-67",
      "id": "fn1-67"
    }
  }, [_vm._v("67")])]), _vm._v(" In September 1940, Japan secured bases in northern French Indochina (especially Tonkin)."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-68",
      "id": "fn1-68"
    }
  }, [_vm._v("68")])]), _vm._v(" The 5th Japanese Division under Lieutenant General Akihito Nakamura from south China invaded Vietnam and seized the railroads. After 25 September, the Japanese controlled all of northern Vietnam south of the Chinese border to Hanoi."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-69",
      "id": "fn1-69"
    }
  }, [_vm._v("69")])]), _vm._v(" This not only allowed them to cut the lifeline of the KMT (Kuomintang) from Haiphong to Chungking but also to move their forces within striking distance of Thailand and Malaya."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-70",
      "id": "fn1-70"
    }
  }, [_vm._v("70")])]), _vm._v(" The Japanese move into Indochina, and especially southern Indochina, put the Philippines within the orbit of long-range Japanese bombers. And this move triggered US economic sanctions against Japan, which later led to the Japanese attack on Pearl Harbor.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("On the basis of CoSC’s appreciation of July 1940, the three British military branches—the army, navy, and RAF—of Malaya generated a tactical appreciation that became the agenda of a conference held at Singapore in October 1940, attended by representatives of all commands in the Far East. The conference recommended that instead of 336 aircraft, 582 aircraft were required. The CoSC "), _c('span', {
    staticClass: "right_1",
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_18",
      "title": "18"
    }
  }, [_vm._v("responded that 582 aircraft was the ideal figure, but taking into consideration the strategic position in Great Britain, Malta, and the Middle East, the 336 figure should be considered."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-71",
      "id": "fn1-71"
    }
  }, [_vm._v("71")])])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Air Chief Marshal Robert Brooke-Popham, who had retired from the RAF in 1937 and had been appointed as the governor of Kenya, was made commander in chief Far East on 18 November 1940. His Far Eastern Command included the aerial and land forces in Burma, Malaya, Borneo, and Hong Kong."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-72",
      "id": "fn1-72"
    }
  }, [_vm._v("72")])]), _vm._v(" In 1936, the headquarters of the naval commander and the army headquarters at Fort Canning were in Singapore city. However, the RAF headquarters was at Seletar on the north side of the island. Until November 1940, the three services worked independently, and the commanders of the army and air force were responsible directly to their own ministries. The senior naval officer at Singapore was responsible for the naval defense of Singapore Island and local defense of the surrounding waters. Later, as rear admiral Malaya, he became responsible for the security of the Malayan coastline. From July 1940 onwards, the naval commander in chief China Station flew his flag on Singapore and assumed responsibility for all waters off the coast of Malaya, except the responsibility for local defense of Singapore, which remained with the rear admiral Malaya. Brooke-Popham was responsible to the Chiefs of Staff for operational control in Malaya, Burma, and Hong Kong and for coordination of plans for the defense of these territories. However, GOC Malaya was to correspond with the War Office on all matters that he had hitherto dealt with. The commander in chief Far East had no control over the naval forces, nor did he have any administrative control. The various commands continued to deal with their respective ministries. The commander in chief Far East had only a small operational staff and no administrative staff."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-73",
      "id": "fn1-73"
    }
  }, [_vm._v("73")])]), _vm._v(" Further, the Combined Intelligence Bureau on which the three services relied for information of Japanese intentions and troop movements remained under the control of the RN and not under Brooke-Popham. And the RN displayed reluctance in sharing all the information with the other two sister services."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-74",
      "id": "fn1-74"
    }
  }, [_vm._v("74")])]), _vm._v(" Thus, unified command in the true sense of the term did not apply in the case of Brooke-Popham’s command. Despite his title, Brooke-Popham’s power was limited. Lack of unity of command hampered the defensive preparations.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("The Far East Combined Bureau (Intelligence) under the commander in chief China was located at the naval base. The commander in chief Far East was dependent on this bureau for intelligence, so he established his headquarters at the naval base with the aim of moving to the Sime Road area in case of war breaking out. Just before the outbreak of the Second World War in the Far East (the Pacific War), the RAF’s new headquarters was completed at Sime Road near the golf club in the center of the island. Thus, when the war started, army headquarters, RAF headquarters, and the civilian government were grouped in one area, while those of the two commanders in chief and of the rear admiral Malaya were in another area some ten miles away. No unified command headquarter was ever created."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-75",
      "id": "fn1-75"
    }
  }, [_vm._v("75")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Brooke-Popham was told by London that the requirements of Britain’s defense, the Battle of the Atlantic, the Middle East, Russia, and Iraq and Iran took precedence over the Far East. Brooke-Popham accepted that his principal task "), _c('span', {
    staticClass: "right_1",
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_19",
      "title": "19"
    }
  }, [_vm._v("was to protect the naval base and to a lesser degree Malaya for its rubber and tin production. The hostile aircraft had to be kept away from bombing the Singapore Naval Base. This, he reasoned, required extending the defensive line right up to the northern end of Malaya. He demanded a minimum of 336 aircraft."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-76",
      "id": "fn1-76"
    }
  }, [_vm._v("76")])]), _vm._v(" Brooke-Popham’s scheme of defense for Singapore included construction of modern airfields in the northeast coast of Malaya and Borneo."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-77",
      "id": "fn1-77"
    }
  }, [_vm._v("77")])]), _vm._v(" The protection of these airfields was the duty of the army, but the army was not taken into confidence in the scheme involving selection and construction of the aerodromes. Heath asserts that the airfields on the east coast of Malaya were ill chosen from the army’s point of view."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-78",
      "id": "fn1-78"
    }
  }, [_vm._v("78")])]), _vm._v(" In fact during 1936–37, AOC Far East Air Commodore S. W. Smith decided to construct a string of airfields in eastern Malaya."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-79",
      "id": "fn1-79"
    }
  }, [_vm._v("79")])]), _vm._v(" The RAF argued that airfields at Gong Kedah and Machang were necessary for making reconnaissance over Indochina. Constant cloud cover over the central mountain range in Malaya handicapped flight from the west coast of Malaya toward the east coast and the South China Sea. Reconnaissance over the South China Sea was very important because it was from this direction that the threat to Malaya by sea was greatest. Hence, a number of airfields along the east coast of Malaya were absolutely necessary."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-80",
      "id": "fn1-80"
    }
  }, [_vm._v("80")])]), _vm._v(" Only from northeast Malaya could British aircraft reach southwest Indochina. The short range of available aircraft, especially the Vildebeeste, also forced the RAF to establish airfields on the not-so-defensible northeast Malaya."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-81",
      "id": "fn1-81"
    }
  }, [_vm._v("81")])]), _vm._v(" The War Office counterargued that the army lacked the necessary manpower to garrison these airfields."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-82",
      "id": "fn1-82"
    }
  }, [_vm._v("82")])]), _vm._v(" Heath goes on to say that the airfields on the east coast of Malaya were vulnerable to amphibious assault. The airfields were neither dispersed nor concealed, and they were not constructed among the paddy fields in the interior, to avoid the hassle or expense of the acquisition of the land."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-83",
      "id": "fn1-83"
    }
  }, [_vm._v("83")])]), _vm._v(" British military officer turned historian Louis Allen agrees with Heath’s criticism."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-84",
      "id": "fn1-84"
    }
  }, [_vm._v("84")])]), _vm._v(" Heath notes that there was a lack of cooperation between the RAF and the army in preparing the defensive scheme."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-85",
      "id": "fn1-85"
    }
  }, [_vm._v("85")])])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("When Brooke-Popham was appointed to his high-sounding post, there were only eighty-eight RAF aircraft in the Malaya-Singapore theater. Of them, twenty-four were Blenheim bombers, twenty-four Hudsons, and twenty-four obsolete Vildebeeste torpedo bombers. In addition there were twelve Wirraways and four flying boats."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-86",
      "id": "fn1-86"
    }
  }, [_vm._v("86")])]), _vm._v(" In December 1940, Robert Menzies, the Australian prime minister, unsuccessfully tried to persuade Churchill to transfer three or four vessels from the Mediterranean Fleet to Singapore."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-87",
      "id": "fn1-87"
    }
  }, [_vm._v("87")])]), _vm._v(" On 10 January 1941, the Chiefs of Staff estimated that though 582 aircraft was the ideal number for holding Malaya, 336 aircraft could be delivered before the end of 1941."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-88",
      "id": "fn1-88"
    }
  }, [_vm._v("88")])]), _vm._v(" On 24 April 1941, Air Vice Marshal C. W. Pulford became AOC RAF Far East Command. He held his command till 11 February 1942, and two days later he left Singapore. By midsummer 1941, the Far East Command RAF included Hong Kong, Borneo, Malaya, Ceylon/Sri Lanka, and Burma, with headquarters at Singapore. The command stretched across the Indian Ocean to Durban and Mombasa. The principal functions of this command were to protect the naval base at Singapore and to cooperate with the RN to ensure security of the SLoC in the Indian Ocean and South China Sea."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-89",
      "id": "fn1-89"
    }
  }, [_vm._v("89")])]), _vm._v(" In the autumn of 1941, more than one hundred "), _c('span', {
    staticClass: "right_1",
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_20",
      "title": "20"
    }
  }, [_vm._v("RAF squadrons, twenty-five divisions, and half the RN battle fleet were guarding Britain."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-90",
      "id": "fn1-90"
    }
  }, [_vm._v("90")])]), _vm._v(" During the first half of 1941, Middle East Command received 1,300 aircraft (724 fighters, 421 light bombers, and 108 heavy bombers)."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-91",
      "id": "fn1-91"
    }
  }, [_vm._v("91")])]), _vm._v(" Even in the autumn of 1941, GHQ concluded that the RAF would be able to destroy 40 percent of the hostile landing force in Malaya before it could be landed. When the Japanese attack started, the RAF had only 158 machines in Malaya."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-92",
      "id": "fn1-92"
    }
  }, [_vm._v("92")])]), _vm._v(" Historian John R. Ferris asserts that the presence of 300 Spitfires and Hurricanes in Singapore would have deterred the Japanese."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-93",
      "id": "fn1-93"
    }
  }, [_vm._v("93")])]), _vm._v(" Such an assertion is questionable, but one thing is certain: the presence of a substantial number of Hurricanes and Spitfires in Malaya-Singapore during 1941 would surely have prevented the Japanese from gaining air superiority so easily, and this would have significantly changed the course of the land campaign.")])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("On 16 May 1941, Percival became GOC Malaya in place of Bond. He had served as chief of staff Malaya Command (General Staff Officer/GSO 1st) in 1936–37. On 10 July 1941, Group Captain E. B. Rice became the fighter defence commander of Singapore and coordinator of air defences of Malaya."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-94",
      "id": "fn1-94"
    }
  }, [_vm._v("94")])]), _vm._v(" And the commander in chief China Station was Vice Admiral Geoffrey Layton. Rear Admiral Malaya was Vice Admiral Ernest John Spooner."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-95",
      "id": "fn1-95"
    }
  }, [_vm._v("95")])]), _vm._v(" On 23 July, the Vichy authorities bowed to Japanese demands for the use of ports and airfields in southern Indochina."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-96",
      "id": "fn1-96"
    }
  }, [_vm._v("96")])]), _vm._v(" On 18 October 1941, Hideki Tojo became the prime minister of Japan."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-97",
      "id": "fn1-97"
    }
  }, [_vm._v("97")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Major General B. W. Key, CO of the 8th Indian Brigade, asserts that without five infantry divisions, 350 aerial machines, and naval support, Malaya was indefensible."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-98",
      "id": "fn1-98"
    }
  }, [_vm._v("98")])]), _vm._v(" Just before the outbreak of war in the Far East, the RAF had the necessary infrastructure (airfields, repair stations, etc.) but lacked the adequate number of aircraft."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#fr1-99",
      "id": "fn1-99"
    }
  }, [_vm._v("99")])]), _vm._v(" It is to be noted that the Japanese enjoyed aerial and naval superiority in the Malaya-Singapore theater, but the Allied forces had numerical superiority as far as ground force was concerned. So the issue of why the Allied land forces in Malaya were not able to translate their numerical superiority into military power is worth examining.")]), _vm._v(" "), _c('p', {
    staticClass: "h2"
  }, [_c('b', [_vm._v("Conclusion")])]), _vm._v(" "), _c('p', {
    staticClass: "noindent"
  }, [_vm._v("Until the end of the First World War, there was no strategic threat to the British-dominated Malaya-Singapore region. The rise of Japan as a potential enemy after 1919 resulted in the British decision to develop a naval base in the Singapore Island. The British government lacked a long-term consistent policy for defending Malaya-Singapore. The British strategic managers could not decide on the nature (aerial or amphibious, etc.) and direction (to the island or north Malaya) of the Japanese threat to Singapore. In the late 1930s, as the Japanese threat grew in the Far East, simultaneous with the resurgence of Italy and especially Germany in Europe, Britain’s overall strategic scenario deteriorated. And absence of a unified command in the Malaya-Singapore theater further obstructed military effectiveness of the Allied air-land-sea forces deployed in this region. When all was said and done, the Japanese did not enjoy any numerical superiority in land forces, and the British imperial armies fought badly in Malaya and Singapore. Why did "), _c('span', {
    staticClass: "right_1",
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_21",
      "title": "21"
    }
  }, [_vm._v("the Allied land force fold up so easily and quickly? Let us focus on the organization of the Allied ground force, especially its biggest component, the Indian Army. This is the subject of our next chapter.")])]), _vm._v(" "), _c('p', {
    staticClass: "noteh"
  }, [_c('b', [_vm._v("Notes")])]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-1"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-1"
    }
  }, [_vm._v("1")]), _vm._v(". James Neidpath, "), _c('i', [_vm._v("The Singapore Naval Base and the Defence of Britain’s Eastern Empire, 1919–1941")]), _vm._v(" (Oxford: Clarendon, 1981), 2, 9.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-2"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-2"
    }
  }, [_vm._v("2")]), _vm._v(". Neidpath, "), _c('i', [_vm._v("Singapore Naval Base,")]), _vm._v(" 13.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-3"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-3"
    }
  }, [_vm._v("3")]), _vm._v(". Malcolm H. Murfett et al., "), _c('i', [_vm._v("Between Two Oceans: A Military History of Singapore from 1275 to 1971")]), _vm._v(" (1999; repr., Singapore: Marshall Cavendish, 2011), 36–61.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-4"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-4"
    }
  }, [_vm._v("4")]), _vm._v(". Nadzan Haron, “Colonial Defence and British Approach to the Problems in Malaya: 1874–1918,” "), _c('i', [_vm._v("Modern Asian Studies")]), _vm._v(" 24, no. 2 (1990): 279–80.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-5"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-5"
    }
  }, [_vm._v("5")]), _vm._v(". Ong Chit Chung, "), _c('i', [_vm._v("Operation Matador: World War II, Britain’s Attempt to Foil the Japanese Invasion of Malaya and Singapore")]), _vm._v(" (1997; repr., Singapore: Marshall Cavendish, 2003), 5.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-6"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-6"
    }
  }, [_vm._v("6")]), _vm._v(". Louis Allen, "), _c('i', [_vm._v("Singapore: 1941–1942")]), _vm._v(" (1977; repr., Oxon: Frank Cass, 2005), 38.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-7"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-7"
    }
  }, [_vm._v("7")]), _vm._v(". Kwong Chi Man and Tsoi Yiu Lun, "), _c('i', [_vm._v("Eastern Fortress: A Military History of Hong Kong, 1840–1970")]), _vm._v(" (Hong Kong: Hong Kong University Press, 2014), 73, 75.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-8"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-8"
    }
  }, [_vm._v("8")]), _vm._v(". Christopher M. Bell, “‘Our Most Exposed Outpost’: Hong Kong and British Far Eastern Strategy, 1921–1941,” "), _c('i', [_vm._v("Journal of Military History")]), _vm._v(" 60 (1996): 61.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-9"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-9"
    }
  }, [_vm._v("9")]), _vm._v(". Martin Thomas, “Disaster Foreseen? France and the Fall of Singapore,” in "), _c('i', [_vm._v("Sixty Years On: The Fall of Singapore Revisited")]), _vm._v(", ed. Brian Farrell and Sandy Hunter (2002; repr., Singapore: Eastern Universities Press, 2003), 70.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-10"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-10"
    }
  }, [_vm._v("10")]), _vm._v(". Neidpath, "), _c('i', [_vm._v("Singapore Naval Base,")]), _vm._v(" 13, 36, 47, 51.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-11"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-11"
    }
  }, [_vm._v("11")]), _vm._v(". Neidpath, "), _c('i', [_vm._v("Singapore Naval Base,")]), _vm._v(" 38.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-12"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-12"
    }
  }, [_vm._v("12")]), _vm._v(". Raymond Callahan, "), _c('i', [_vm._v("The Worst Disaster: The Fall of Singapore")]), _vm._v(" (1977; repr., Singapore: Cultured Lotus, 2001), 23.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-13"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-13"
    }
  }, [_vm._v("13")]), _vm._v(". Karl Hack and Kevin Blackburn, "), _c('i', [_vm._v("Did Singapore Have to Fall? Churchill and the Impregnable Fortress")]), _vm._v(" (2004; repr., Oxon, UK: Routledge, 2008), 30.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-14"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-14"
    }
  }, [_vm._v("14")]), _vm._v(". Chung, "), _c('i', [_vm._v("Operation Matador")]), _vm._v(", 11; Allen, "), _c('i', [_vm._v("Singapore")]), _vm._v(", 37; Antony Best, "), _c('i', [_vm._v("British Intelligence and the Japanese Challenge in Asia, 1914–1941")]), _vm._v(" (Basingstoke, UK: Palgrave Macmillan, 2002), 14–15.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-15"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-15"
    }
  }, [_vm._v("15")]), _vm._v(". Neidpath, "), _c('i', [_vm._v("Singapore Naval Base,")]), _vm._v(" 42, 51.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-16"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-16"
    }
  }, [_vm._v("16")]), _vm._v(". Note on the Malayan Campaign by LMH, LMH 5, P 441, Lewis Heath Papers, IWM, London, 1; Hack and Blackburn, "), _c('i', [_vm._v("Did Singapore")]), _vm._v(", 31; Chung, "), _c('i', [_vm._v("Operation Matador")]), _vm._v(", 13, 29.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-17"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-17"
    }
  }, [_vm._v("17")]), _vm._v(". Chung, "), _c('i', [_vm._v("Operation Matador")]), _vm._v(", 49.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-18"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-18"
    }
  }, [_vm._v("18")]), _vm._v(". Hack and Blackburn, "), _c('i', [_vm._v("Did Singapore")]), _vm._v(", 14.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-19"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-19"
    }
  }, [_vm._v("19")]), _vm._v(". Neidpath, "), _c('i', [_vm._v("Singapore Naval Base,")]), _vm._v(" 47.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-20"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-20"
    }
  }, [_vm._v("20")]), _vm._v(". Lieutenant General A. E. Percival, “Operations of Malaya Command from 8 December 1941 to 15 February 1942,” "), _c('i', [_vm._v("Second Supplement to the London Gazette")]), _vm._v(", February 26, 1948 (London: HMSO, 1948), 1249; Neidpath, "), _c('i', [_vm._v("Singapore Naval Base,")]), _vm._v(" 51.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-21"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-21"
    }
  }, [_vm._v("21")]), _vm._v(". Callahan, "), _c('i', [_vm._v("Worst Disaster")]), _vm._v(", 22–23; Allen, "), _c('i', [_vm._v("Singapore")]), _vm._v(", 38; Man and Lun, "), _c('i', [_vm._v("Eastern Fortress")]), _vm._v(", 74.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-22"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-22"
    }
  }, [_vm._v("22")]), _vm._v(". Bell, “‘Our Most Exposed Outpost,’” 66.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-23"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-23"
    }
  }, [_vm._v("23")]), _vm._v(". Chung, "), _c('i', [_vm._v("Operation Matador")]), _vm._v(", 36, 39.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-24"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-24"
    }
  }, [_vm._v("24")]), _vm._v(". Henry Probert, "), _c('i', [_vm._v("The Forgotten Air Force: The Royal Air Force in the War against Japan, 1941–45")]), _vm._v(" (London: Brassey’s, 1995), 1.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-25"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-25"
    }
  }, [_vm._v("25")]), _vm._v(". "), _c('span', {
    staticClass: "right_1",
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_22",
      "title": "22"
    }
  }, [_vm._v("Notes by Lieutenant General A. E. Percival on the Brief Outline Narrative of the Mainland Operations in Malaya 8 Dec. 1941 to 31 Jan. 1942, Prepared by the Combined Inter-Services Historical Section (India), June 1947, Percival Papers, IWM, 1.")])]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-26"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-26"
    }
  }, [_vm._v("26")]), _vm._v(". Probert, "), _c('i', [_vm._v("Forgotten Air Force")]), _vm._v(", 2–3.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-27"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-27"
    }
  }, [_vm._v("27")]), _vm._v(". Percival, “Operations,” 1249.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-28"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-28"
    }
  }, [_vm._v("28")]), _vm._v(". Percival, “Operations,” 1249.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-29"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-29"
    }
  }, [_vm._v("29")]), _vm._v(". Peter Thompson, "), _c('i', [_vm._v("The Battle for Singapore: The True Story of the Greatest Catastrophe of World War II")]), _vm._v(" (2005; repr., London: Piatkus, 2013), 37.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-30"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-30"
    }
  }, [_vm._v("30")]), _vm._v(". Hack and Blackburn, "), _c('i', [_vm._v("Did Singapore")]), _vm._v(", 31.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-31"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-31"
    }
  }, [_vm._v("31")]), _vm._v(". Percival, “Operations,” 1249.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-32"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-32"
    }
  }, [_vm._v("32")]), _vm._v(". Allen, "), _c('i', [_vm._v("Singapore")]), _vm._v(", 41.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-33"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-33"
    }
  }, [_vm._v("33")]), _vm._v(". Callahan, "), _c('i', [_vm._v("Worst Disaster")]), _vm._v(", 24.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-34"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-34"
    }
  }, [_vm._v("34")]), _vm._v(". Saki Dockrill, “Britain’s Grand Strategy and Anglo-American Leadership in the War against Japan,” in "), _c('i', [_vm._v("British and Japanese Military Leadership in the Far Eastern War, 1941–1945")]), _vm._v(", ed. Brian Bond and Kyoichi Tachikawa (London: Frank Cass, 2004), 10; Dan Van Der Vat, "), _c('i', [_vm._v("Standard of Power: The Royal Navy in the Twentieth Century")]), _vm._v(" (2000; repr., London: Pimlico, 2001), 154–55.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-35"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-35"
    }
  }, [_vm._v("35")]), _vm._v(". Bill Yenne, "), _c('i', [_vm._v("The Imperial Japanese Army: The Invincible Years 1941–42")]), _vm._v(" (Oxford: Osprey, 2014), 79.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-36"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-36"
    }
  }, [_vm._v("36")]), _vm._v(". Christopher M. Bell, “The ‘Singapore Strategy’ and the Deterrence of Japan: Winston Churchill, the Admiralty and the Dispatch of Force Z,” "), _c('i', [_vm._v("English Historical Review")]), _vm._v(" 116, no. 467 (2001): 607.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-37"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-37"
    }
  }, [_vm._v("37")]), _vm._v(". Letter from Lieutenant General A. E. Percival to Major D. N. Kann, War Office, London, January 16, 1949, Percival Papers.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-38"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-38"
    }
  }, [_vm._v("38")]), _vm._v(". Comments made by Percival on the Notes on the Malayan Campaign by LMH, LMH 5, P 441, Lewis Heath Papers.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-39"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-39"
    }
  }, [_vm._v("39")]), _vm._v(". Percival, “Operations,” 1249.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-40"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-40"
    }
  }, [_vm._v("40")]), _vm._v(". Hack and Blackburn, "), _c('i', [_vm._v("Did Singapore")]), _vm._v(", 39.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-41"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-41"
    }
  }, [_vm._v("41")]), _vm._v(". A. E. Percival, Deductions from Japanese Appreciation of the Attack on the Fortress of Singapore, 1937, Percival Papers.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-42"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-42"
    }
  }, [_vm._v("42")]), _vm._v(". Thompson, "), _c('i', [_vm._v("Battle for Singapore")]), _vm._v(", 7.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-43"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-43"
    }
  }, [_vm._v("43")]), _vm._v(". Chung, "), _c('i', [_vm._v("Operation Matador")]), _vm._v(", 6.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-44"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-44"
    }
  }, [_vm._v("44")]), _vm._v(". Hack and Blackburn, "), _c('i', [_vm._v("Did Singapore")]), _vm._v(", 40.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-45"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-45"
    }
  }, [_vm._v("45")]), _vm._v(". Percival, “Operations,” 1250.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-46"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-46"
    }
  }, [_vm._v("46")]), _vm._v(". Murfett et al., "), _c('i', [_vm._v("Between Two Oceans")]), _vm._v(", 170.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-47"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-47"
    }
  }, [_vm._v("47")]), _vm._v(". Thompson, "), _c('i', [_vm._v("Battle for Singapore")]), _vm._v(", 49.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-48"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-48"
    }
  }, [_vm._v("48")]), _vm._v(". Malcolm H. Murfett, “Reflections on an Enduring Theme: The ‘Singapore Strategy’ at Sixty,” in Farrell and Hunter, "), _c('i', [_vm._v("Sixty Years On")]), _vm._v(", 16.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-49"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-49"
    }
  }, [_vm._v("49")]), _vm._v(". Ong Chit Chung, “Major-General William Dobbie and the Defence of Malaya, 1935–38,” "), _c('i', [_vm._v("Journal of Southeast Asian Studies")]), _vm._v(" 17, no. 2 (1986): 285.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-50"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-50"
    }
  }, [_vm._v("50")]), _vm._v(". Allen, "), _c('i', [_vm._v("Singapore")]), _vm._v(", 45.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-51"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-51"
    }
  }, [_vm._v("51")]), _vm._v(". Raymond Callahan, “Churchill and Singapore,” in Farrell and Hunter, "), _c('i', [_vm._v("Sixty Years On")]), _vm._v(", 158.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-52"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-52"
    }
  }, [_vm._v("52")]), _vm._v(". Chung, “Major-General William Dobbie,” 286.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-53"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-53"
    }
  }, [_vm._v("53")]), _vm._v(". Man and Lun, "), _c('i', [_vm._v("Eastern Fortress")]), _vm._v(", 75–76.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-54"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-54"
    }
  }, [_vm._v("54")]), _vm._v(". Greg Kennedy, “Symbol of Imperial Defence: The Role of Singapore in British and American Far Eastern Strategic Relations, 1933–1941,” in Farrell and Hunter, "), _c('i', [_vm._v("Sixty Years On")]), _vm._v(", 61.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-55"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-55"
    }
  }, [_vm._v("55")]), _vm._v(". "), _c('span', {
    staticClass: "right_1",
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_23",
      "title": "23"
    }
  }, [_vm._v("John R. Ferris, “Student and Master: The United Kingdom, Japan, Airpower, and the Fall of Singapore, 1920–1941,” in Farrell and Hunter, "), _c('i', [_vm._v("Sixty Years On")]), _vm._v(", 114.")])]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-56"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-56"
    }
  }, [_vm._v("56")]), _vm._v(". Callahan, "), _c('i', [_vm._v("Worst Disaster")]), _vm._v(", 26.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-57"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-57"
    }
  }, [_vm._v("57")]), _vm._v(". Allen, "), _c('i', [_vm._v("Singapore")]), _vm._v(", 48; Callahan, "), _c('i', [_vm._v("Worst Disaster")]), _vm._v(", 26.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-58"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-58"
    }
  }, [_vm._v("58")]), _vm._v(". Memorandum by the Chiefs of Staff Committee, March 18, 1940, WP(40)102, Cabinet Papers (hereafter CP), NMML, New Delhi.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-59"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-59"
    }
  }, [_vm._v("59")]), _vm._v(". Reports for the Month of May 1940 for the Dominions, India, Burma and the Colonies, Protectorates and Mandated Territories, Report by the Secy. of State for India, Para 26, June 6, 1940, WP®(40), 164, CP, NMML.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-60"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-60"
    }
  }, [_vm._v("60")]), _vm._v(". Notes by Lieutenant General A. E. Percival on Captain Russell Grenfell’s Draft, July 1950, Percival Papers; Percival, “Operations,” 1250; Murfett et al., "), _c('i', [_vm._v("Between Two Oceans")]), _vm._v(", 173–74.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-61"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-61"
    }
  }, [_vm._v("61")]), _vm._v(". Colin Smith, "), _c('i', [_vm._v("Singapore Burning: Heroism and Surrender in World War II")]), _vm._v(" (2005; repr., London: Penguin, 2006), 29–30.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-62"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-62"
    }
  }, [_vm._v("62")]), _vm._v(". Murfett et al., "), _c('i', [_vm._v("Between Two Oceans")]), _vm._v(", 173.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-63"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-63"
    }
  }, [_vm._v("63")]), _vm._v(". Report by the Chiefs of Staff Committee, War Cabinet, Immediate Measures Required in the Far East, 25 June 1940, WP(40)222, CAB/66/9/2, PRO, Kew, London, 2.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-64"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-64"
    }
  }, [_vm._v("64")]), _vm._v(". Report by the Chiefs of Staff, Far Eastern Policy, War Cabinet, WP(40)289, CAB 66/10/20, PRO, Kew, 1.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-65"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-65"
    }
  }, [_vm._v("65")]), _vm._v(". Air Vice Marshal Paul Maltby, “Report on the Air Operations during the Campaigns in Malaya and Netherland East Indies from 8 December 1941 to 12 March 1942,” "), _c('i', [_vm._v("Third Supplement to the London Gazette")]), _vm._v(", February 26, 1948 (London: HMSO, 1948), 1349.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-66"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-66"
    }
  }, [_vm._v("66")]), _vm._v(". Thomas, “Disaster Foreseen?,” 69.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-67"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-67"
    }
  }, [_vm._v("67")]), _vm._v(". Ken Kotani, “Pearl Harbor: Japanese Planning and Command Structure,” in "), _c('i', [_vm._v("The Pacific War: From Pearl Harbor to Hiroshima")]), _vm._v(", ed. Daniel Marston (2005; repr., Oxford: Osprey, 2010), 33.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-68"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-68"
    }
  }, [_vm._v("68")]), _vm._v(". Hack and Blackburn, "), _c('i', [_vm._v("Did Singapore")]), _vm._v(", 39.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-69"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-69"
    }
  }, [_vm._v("69")]), _vm._v(". Yenne, "), _c('i', [_vm._v("Imperial Japanese Army")]), _vm._v(", 70.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-70"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-70"
    }
  }, [_vm._v("70")]), _vm._v(". Thompson, "), _c('i', [_vm._v("Battle for Singapore")]), _vm._v(", 55.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-71"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-71"
    }
  }, [_vm._v("71")]), _vm._v(". Maltby, “Report,” 1349.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-72"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-72"
    }
  }, [_vm._v("72")]), _vm._v(". Ian Morrison, "), _c('i', [_vm._v("Malayan Postscript")]), _vm._v(" (London: Faber and Faber, 1942), 12–13.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-73"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-73"
    }
  }, [_vm._v("73")]), _vm._v(". Percival, “Operations,” 1247.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-74"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-74"
    }
  }, [_vm._v("74")]), _vm._v(". Timothy Hall, "), _c('i', [_vm._v("The Fall of Singapore")]), _vm._v(" (1983, repr., Oxon: Routledge, 2015), 14.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-75"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-75"
    }
  }, [_vm._v("75")]), _vm._v(". Percival, “Operations,” 1248.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-76"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-76"
    }
  }, [_vm._v("76")]), _vm._v(". Despatch on the Far East by Air Chief Marshal Robert Brooke-Popham, Commander-in-Chief Far East (17 Oct. 1940–27 Dec. 1941), September 8, 1942, CAB/66/28/33, PRO, 5–6, 8.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-77"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-77"
    }
  }, [_vm._v("77")]), _vm._v(". Note on the Malayan Campaign by LMH, Heath Papers, 2; Thompson, "), _c('i', [_vm._v("Battle for Singapore")]), _vm._v(", 56.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-78"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-78"
    }
  }, [_vm._v("78")]), _vm._v(". Note on the Malayan Campaign by LMH, Heath Papers, 3.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-79"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-79"
    }
  }, [_vm._v("79")]), _vm._v(". Callahan, "), _c('i', [_vm._v("Worst Disaster")]), _vm._v(", 27–28.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-80"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-80"
    }
  }, [_vm._v("80")]), _vm._v(". Maltby, “Report,” 1350.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-81"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-81"
    }
  }, [_vm._v("81")]), _vm._v(". Note on the Malayan Campaign by LMH, Part 1, Heath Papers, 4; Comments by Air Chief Marshal R. Brooke-Popham on Major-General Percival’s Despatch “Operations of Malaya Command,” July 28, 1946, Percival Papers, 1.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-82"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-82"
    }
  }, [_vm._v("82")]), _vm._v(". Thompson, "), _c('i', [_vm._v("Battle for Singapore")]), _vm._v(", 38.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-83"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-83"
    }
  }, [_vm._v("83")]), _vm._v(". Note on the Malayan Campaign by LMH, Heath Papers, 4a–4b.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-84"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-84"
    }
  }, [_vm._v("84")]), _vm._v(". "), _c('span', {
    staticClass: "right_1",
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_24",
      "title": "24"
    }
  }, [_vm._v("Allen, "), _c('i', [_vm._v("Singapore")]), _vm._v(", 41.")])]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-85"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-85"
    }
  }, [_vm._v("85")]), _vm._v(". Note on the Malayan Campaign by LMH, Part 1, 6.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-86"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-86"
    }
  }, [_vm._v("86")]), _vm._v(". Thompson, "), _c('i', [_vm._v("Battle for Singapore")]), _vm._v(", 59.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-87"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-87"
    }
  }, [_vm._v("87")]), _vm._v(". Bell, “The ‘Singapore Strategy’ and the Deterrence of Japan: Winston Churchill, the Admiralty and the Dispatch of Force Z,” 619.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-88"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-88"
    }
  }, [_vm._v("88")]), _vm._v(". Allen, "), _c('i', [_vm._v("Singapore")]), _vm._v(", 50.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-89"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-89"
    }
  }, [_vm._v("89")]), _vm._v(". Maltby, “Report,” 1347–48.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-90"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-90"
    }
  }, [_vm._v("90")]), _vm._v(". Brian P. Farrell, “1941: An Overview,” in Farrell and Hunter, "), _c('i', [_vm._v("Sixty Years On")]), _vm._v(", 176.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-91"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-91"
    }
  }, [_vm._v("91")]), _vm._v(". Callahan, "), _c('i', [_vm._v("Worst Disaster")]), _vm._v(", 95.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-92"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-92"
    }
  }, [_vm._v("92")]), _vm._v(". Allen, "), _c('i', [_vm._v("Singapore")]), _vm._v(", 51.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-93"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-93"
    }
  }, [_vm._v("93")]), _vm._v(". Ferris, “Student and Master,” 13–14.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-94"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-94"
    }
  }, [_vm._v("94")]), _vm._v(". Maltby, “Report,” 1347.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-95"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-95"
    }
  }, [_vm._v("95")]), _vm._v(". Percival, “Operations,” 1247–48.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-96"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-96"
    }
  }, [_vm._v("96")]), _vm._v(". Callahan, "), _c('i', [_vm._v("Worst Disaster")]), _vm._v(", 97.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-97"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-97"
    }
  }, [_vm._v("97")]), _vm._v(". Yenne, "), _c('i', [_vm._v("Imperial Japanese Army")]), _vm._v(", 73.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-98"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-98"
    }
  }, [_vm._v("98")]), _vm._v(". Major General B. W. Key, 11th Indian Division, p. 456, IWM, 4.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "fr1-99"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#fn1-99"
    }
  }, [_vm._v("99")]), _vm._v(". Probert, "), _c('i', [_vm._v("Forgotten Air Force")]), _vm._v(", 12.")])])])])
}]}


//////////////////
// WEBPACK FOOTER
// ../node_modules/vue-loader/lib/template-compiler?{"id":"data-v-17f55216","hasScoped":false,"transformToRequire":{"video":"src","source":"src","img":"src","image":"xlink:href"}}!../node_modules/vue-loader/lib/selector.js?type=template&index=0!./src/components/text/9780253044174_Chapter_01.vue
// module id = 96
// module chunks = 0